import React, { useState, useEffect, useId } from 'react';
import { AlertCircle, Calendar, Clock, Info, RotateCcw, Users, XCircle } from 'lucide-react';
import { format } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from './ui/dialog';
import { Button } from './ui/button';

// Les deux boutons d'un pied de modal ont la même taille ET la même largeur.
// DESIGN-SYSTEM.md, section Boutons.
//
// 210 px et non 168 : le libellé de confirmation est plus long ici, et c'est
// la largeur du PLUS LONG des deux qui fixe celle des deux. Un bouton qui
// déborde son voisin casse la rangée.
const BOUTON_PIED = 'h-9 min-w-[210px] justify-center gap-2';

const CHAMP = 'w-full rounded-lg border border-gray-200 px-3 py-2 text-sm '
  + 'focus:border-[#246990] focus:outline-none focus:ring-2 focus:ring-[#246990]/20 transition-colors';

const CancelTimeSlotModal = ({
  isOpen,
  onClose,
  onSubmit,
  event,
  mode = 'cancel', // 'cancel' ou 'reactivate'
  anneeScolaire
}) => {
  const [scope, setScope] = useState('single');
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [motif, setMotif] = useState('');
  // Ce qui bloque l'envoi s'écrit DANS la page : il y avait ici trois
  // `alert()` natifs, bloquants et hors charte.
  const [messageErreur, setMessageErreur] = useState(null);

  const idMotif = useId();
  const idDateDebut = useId();
  const idDateFin = useId();

  const isReactivation = mode === 'reactivate';

  const borneDebutAnnee = anneeScolaire?.date_debut
    ? String(anneeScolaire.date_debut).slice(0, 10)
    : undefined;
  const borneFinAnnee = anneeScolaire?.date_fin
    ? String(anneeScolaire.date_fin).slice(0, 10)
    : undefined;

  useEffect(() => {
    if (event) {
      setScope('single');
      setDateDebut('');
      setDateFin('');
      setMotif('');
      setMessageErreur(null);
    }
  }, [event]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setMessageErreur(null);

    if (scope === 'period' && (!dateDebut || !dateFin)) {
      setMessageErreur('Choisissez une date de début et une date de fin.');
      return;
    }

    // Comparaison de chaînes « AAAA-MM-JJ » : juste, et sans objet Date qui
    // traverserait un fuseau horaire.
    if (scope === 'period' && dateDebut > dateFin) {
      setMessageErreur('La date de début doit précéder la date de fin.');
      return;
    }

    if (!isReactivation && !motif.trim()) {
      setMessageErreur("Indiquez le motif de l'annulation : il est lu par les familles et les enseignants.");
      return;
    }

    onSubmit({
      scope,
      motif: motif.trim() || null,
      mode,
      ...(scope === 'period' && {
        date_debut: dateDebut,
        date_fin: dateFin
      })
    });
  };

  const handleClose = () => {
    setScope('single');
    setDateDebut('');
    setDateFin('');
    setMotif('');
    setMessageErreur(null);
    onClose();
  };

  if (!event) return null;

  const accent = isReactivation ? '#246990' : '#c66d59';

  const scopeOptions = [
    {
      value: 'single',
      icon: Calendar,
      title: 'Ce créneau uniquement',
      description: `Le créneau du ${format(new Date(event.date), 'dd/MM/yyyy')}, et lui seul`
    },
    {
      value: 'all',
      icon: Users,
      title: "Tous les créneaux de l'année",
      description: 'Tous les créneaux identiques : même jour, même heure, même classe'
    },
    {
      value: 'period',
      icon: Clock,
      title: 'Période personnalisée',
      description: 'Une plage de dates à choisir'
    }
  ];


  return (
    <Dialog open={isOpen} onOpenChange={(ouvert) => { if (!ouvert) handleClose(); }}>
      <DialogContent
        className="w-[95vw] max-w-5xl !max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0"
        // Un motif est en cours de saisie : un clic à côté ne doit pas
        // l'emporter.
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="flex-row items-center gap-3 space-y-0 border-b px-5 py-4 text-left">
          <div
            className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg text-white"
            style={{ backgroundColor: accent }}
          >
            {isReactivation ? <RotateCcw size={18} /> : <XCircle size={18} />}
          </div>
          <div className="min-w-0">
            <DialogTitle className="text-lg font-semibold text-gray-900">
              {isReactivation ? 'Réactiver le créneau' : 'Annuler le créneau'}
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-500">
              {event.className} - {event.title} - {format(new Date(event.date), 'EEEE dd/MM/yyyy à HH:mm')}
            </DialogDescription>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
            <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
              <div>
                <h3 className="mb-3 text-sm font-semibold text-gray-700">
                  Portée de {isReactivation ? 'la réactivation' : "l'annulation"}
                </h3>

                <div className="grid grid-cols-1 gap-3">
                  {scopeOptions.map((option) => {
                    const Icon = option.icon;
                    const isSelected = scope === option.value;

                    return (
                      <div key={option.value}>
                        <label
                          className="relative flex cursor-pointer items-start rounded-lg border p-4 transition-all"
                          style={isSelected
                            ? { borderColor: accent, backgroundColor: `${accent}0d` }
                            : undefined}
                        >
                          <div className="relative mt-0.5 flex items-center">
                            <input
                              type="radio"
                              name="scope"
                              value={option.value}
                              checked={isSelected}
                              onChange={(e) => setScope(e.target.value)}
                              className="sr-only"
                            />
                            <div
                              className={`flex h-5 w-5 items-center justify-center rounded-full border-2 transition-all ${isSelected ? '' : 'border-gray-300'}`}
                              style={isSelected ? { borderColor: accent, backgroundColor: accent } : undefined}
                            >
                              {isSelected && <div className="h-2 w-2 rounded-full bg-white" />}
                            </div>
                          </div>

                          <div
                            className={`mx-4 rounded-lg p-2 ${isSelected ? 'text-white' : 'bg-gray-100 text-gray-600'}`}
                            style={isSelected ? { backgroundColor: accent } : undefined}
                          >
                            <Icon size={18} />
                          </div>

                          <div className="min-w-0 flex-1">
                            <div
                              className="mb-0.5 font-semibold text-gray-900"
                              style={isSelected ? { color: accent } : undefined}
                            >
                              {option.title}
                            </div>
                            <div className="text-sm text-gray-600">{option.description}</div>
                          </div>
                        </label>

                        {option.value === 'period' && isSelected && (
                          <div className="mt-3 grid grid-cols-2 gap-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
                            <div>
                              <label htmlFor={idDateDebut} className="mb-2 block text-xs font-semibold text-gray-700">
                                Date de début
                              </label>
                              <input
                                id={idDateDebut}
                                type="date"
                                value={dateDebut}
                                onChange={(e) => setDateDebut(e.target.value)}
                                min={borneDebutAnnee}
                                max={dateFin || borneFinAnnee}
                                className={CHAMP}
                                required
                              />
                            </div>
                            <div>
                              <label htmlFor={idDateFin} className="mb-2 block text-xs font-semibold text-gray-700">
                                Date de fin
                              </label>
                              <input
                                id={idDateFin}
                                type="date"
                                value={dateFin}
                                onChange={(e) => setDateFin(e.target.value)}
                                min={dateDebut || borneDebutAnnee}
                                max={borneFinAnnee}
                                className={CHAMP}
                                required
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* ⚠️ Ce bloc annonçait « Cette action est irréversible », en
                    vert et avec le mot « Attention », JUSQU'EN RÉACTIVATION :
                    on présentait comme définitif le geste qui répare. Une
                    annulation se défait par la même route et la même colonne. */}
                {scope !== 'single' && (
                  isReactivation ? (
                    <div className="mt-4 flex items-start gap-3 rounded-lg border border-[#246990]/20 bg-[#eef7fb] p-4">
                      <Info className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#246990]" />
                      <div className="text-sm text-[#1f5a7a]">
                        Les créneaux concernés redeviendront actifs. Vous pourrez les annuler de nouveau
                        à tout moment.
                      </div>
                    </div>
                  ) : (
                    <div className="mt-4 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
                      <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
                      <div className="text-sm text-amber-800">
                        <strong>Attention :</strong> cette annulation touchera{' '}
                        {scope === 'all' ? "tous les créneaux récurrents de l'année" : 'plusieurs créneaux'}.
                        Les créneaux annulés restent visibles, barrés, et peuvent être réactivés.
                      </div>
                    </div>
                  )
                )}
              </div>

              <div className="space-y-4 lg:border-l lg:border-gray-200 lg:pl-6">
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <div className="mb-2 flex items-center gap-2 font-semibold text-gray-700">
                    <Calendar size={16} />
                    Créneau concerné
                  </div>
                  <dl className="space-y-1 text-sm text-gray-600">
                    <div><dt className="inline font-semibold">Classe : </dt><dd className="inline">{event.className}</dd></div>
                    <div><dt className="inline font-semibold">Matière : </dt><dd className="inline">{event.title}</dd></div>
                    <div><dt className="inline font-semibold">Enseignant : </dt><dd className="inline">{event.teacher}</dd></div>
                    <div>
                      <dt className="inline font-semibold">Date : </dt>
                      <dd className="inline">{format(new Date(event.date), 'dd/MM/yyyy à HH:mm')}</dd>
                    </div>
                  </dl>
                </div>

                {!isReactivation && (
                  <div>
                    <label htmlFor={idMotif} className="mb-2 block text-sm font-semibold text-gray-700">
                      Motif d'annulation <span className="text-[#c66d59]">*</span>
                    </label>
                    <textarea
                      id={idMotif}
                      value={motif}
                      onChange={(e) => setMotif(e.target.value)}
                      className={`${CHAMP} resize-none`}
                      rows="3"
                      placeholder="Ex : cours reporté, salle indisponible, sortie pédagogique"
                      required
                    />
                    {/* Ce texte sort de l'école : il est lu dans l'espace
                        famille et dans l'espace enseignant. L'écran ne le
                        disait nulle part, et son exemple invitait à écrire
                        l'absence d'un salarié. */}
                    <div className="mt-2 flex items-start gap-2 rounded-lg border border-[#246990]/20 bg-[#eef7fb] px-3 py-2 text-xs text-[#1f5a7a]">
                      <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                      <span>
                        Ce motif est affiché aux familles et aux enseignants. Évitez d'y nommer
                        la situation personnelle d'un membre du personnel.
                      </span>
                    </div>
                  </div>
                )}

                {messageErreur && (
                  <div
                    role="alert"
                    className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700"
                  >
                    <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                    <span>{messageErreur}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <DialogFooter className="border-t bg-gray-50 px-5 py-4">
            {/* « Annuler » à côté d'« Annuler le créneau » : deux sens du même
                mot dans le même pied. Le dépôt a déjà tranché ailleurs
                (AvancesSalaire) - le bouton de sortie s'appelle « Retour ». */}
            <Button type="button" variant="outline" className={BOUTON_PIED} onClick={handleClose}>
              Retour
            </Button>
            <Button
              type="submit"
              className={`${BOUTON_PIED} border border-transparent text-white`}
              style={{ backgroundColor: accent }}
            >
              {isReactivation ? <RotateCcw className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
              {/* Libellé FIXE. Il variait avec la portée (« Annuler tous les
                  créneaux »), s'allongeait, et ne faisait plus la même largeur
                  que « Retour ». La portée est déjà dite deux fois dans le
                  corps : le bouton n'a pas à la répéter. Forme du dépôt,
                  reprise de la fenêtre d'annulation d'une avance. */}
              {isReactivation ? 'Confirmer la réactivation' : "Confirmer l'annulation"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CancelTimeSlotModal;
